import { useCallback, useEffect, useState } from 'react';
import { Icons } from '../components/Icons.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useI18n, useT } from '../lib/i18n.jsx';
import { useToast } from '../lib/toast.jsx';
import { useDialog } from '../lib/dialog.jsx';
import { errorMessage } from '../lib/errors.js';
import * as api from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { subscriptionStatus } from '../lib/subscription.js';

// Where a store subscription is actually managed. We never pretend to cancel
// one of these — Apple and Google own them, and a fake cancel button would
// leave the parent still being billed.
const STORE_LINKS = {
  apple: 'https://apps.apple.com/account/subscriptions',
  google: 'https://play.google.com/store/account/subscriptions',
};

const PENDING_CHARGE_KEY = 'rofoofee.portal.pendingCharge';

// Tap charge states that mean "this is never going to succeed". Anything else
// (INITIATED, CAPTURED-but-unprocessed) is worth waiting on.
const DEAD_CHARGE_STATUSES = new Set([
  'DECLINED', 'FAILED', 'CANCELLED', 'ABANDONED', 'VOID', 'RESTRICTED', 'TIMEDOUT', 'UNKNOWN',
]);

function PlanCard({ plan, lang, busy, onChoose, currentPlan }) {
  const t = useT();
  const isCurrent = currentPlan === plan.slug;
  const title = lang === 'ar' && plan.title_ar ? plan.title_ar : plan.title;

  return (
    <li className={`plan-card${isCurrent ? ' current' : ''}`}>
      <div className="plan-head">
        <strong>{title}</strong>
        {isCurrent && <em className="tag">{t('subscribe.currentPlan')}</em>}
      </div>
      <div className="plan-price">
        <span className="amount" dir="ltr">{plan.display}</span>
        <span className="cur">{plan.currency}</span>
        <small>/ {t(`subscribe.per_${plan.interval}`)}</small>
      </div>
      <button
        type="button"
        className="btn primary block"
        disabled={busy}
        onClick={() => onChoose(plan)}
      >
        {busy ? t('subscribe.starting') : t('subscribe.choose')}
      </button>
    </li>
  );
}

export default function Subscription() {
  const t = useT();
  const { lang } = useI18n();
  const toast = useToast();
  const dialog = useDialog();
  const { user, refresh } = useAuth();

  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // True while we are waiting for the webhook after a return from Tap.
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async (signal) => {
    try {
      const [planRes, subRes] = await Promise.all([
        api.getPlans(signal),
        api.getSubscription(signal).catch(() => null),
      ]);
      setPlans(planRes?.plans ?? []);
      setSub(subRes);
      return subRes;
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error(errorMessage(err, t));
      return null;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const c = new AbortController();
    load(c.signal);
    return () => c.abort();
  }, [load]);

  // Coming back from Tap's hosted page.
  //
  // The redirect proves the customer finished at Tap, NOT that we have been
  // told about it: access is granted by the webhook, which arrives
  // independently and can take several seconds. So poll rather than check
  // once — a single check races the webhook and loses silently, which reads
  // to the customer as "I paid and nothing happened".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('payment')) return undefined;
    window.history.replaceState({}, '', window.location.pathname);

    let cancelled = false;
    let timer = null;
    setConfirming(true);

    let chargeId = '';
    try {
      chargeId = sessionStorage.getItem(PENDING_CHARGE_KEY) || '';
      sessionStorage.removeItem(PENDING_CHARGE_KEY);
    } catch {}

    const deadline = Date.now() + 45000;

    const finish = (fn) => {
      setConfirming(false);
      fn();
    };

    const poll = async () => {
      if (cancelled) return;
      const [next] = await Promise.all([load(), refresh()]);
      if (cancelled) return;

      if (next?.status === 'active') {
        finish(() => toast.success(t('subscribe.paymentConfirmed')));
        return;
      }

      // Nothing granted yet — ask Tap whether the payment actually worked. A
      // decline must be reported as a decline, not left to time out looking
      // like a slow success.
      if (chargeId) {
        try {
          const charge = await api.checkCharge(chargeId);
          if (cancelled) return;
          if (DEAD_CHARGE_STATUSES.has(String(charge?.status || '').toUpperCase())) {
            finish(() => toast.error(t('subscribe.paymentDeclined'), { timeout: 15000 }));
            return;
          }
        } catch {
          // Couldn't ask — fall through and keep waiting on the webhook.
        }
      }

      if (Date.now() >= deadline) {
        // Say so instead of going quiet. The payment may still land — the
        // customer needs to know it is not lost, and what to do next.
        finish(() => toast.info(t('subscribe.paymentPending'), { timeout: 15000 }));
        return;
      }
      timer = setTimeout(poll, 3000);
    };

    timer = setTimeout(poll, 1500);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function choose(plan) {
    setBusy(true);
    try {
      const res = await api.subscribe(plan.slug, {
        redirectUrl: `${window.location.origin}${window.location.pathname}?payment=done`,
        lang,
      });
      if (!res?.payment_url) throw new Error('missing_payment_url');
      // Remember which charge we are waiting on, so the return trip can ask
      // Tap what happened to it rather than only watching for a webhook that
      // will never come if the card was declined.
      try {
        sessionStorage.setItem(PENDING_CHARGE_KEY, res.charge_id || '');
      } catch {}
      // Hand off to Tap's hosted page — card details never touch us.
      window.location.href = res.payment_url;
    } catch (err) {
      toast.error(errorMessage(err, t));
      setBusy(false);
    }
  }

  async function cancel() {
    const ok = await dialog.confirm({
      title: t('subscribe.cancelTitle'),
      message: t('subscribe.cancelBody', {
        date: formatDate(sub?.current_period_end || status.expiresAt),
      }),
      confirmLabel: t('subscribe.cancelConfirm'),
      cancelLabel: t('subscribe.cancelKeep'),
      danger: true,
    });
    if (!ok) return;

    setBusy(true);
    try {
      await api.cancelSubscription();
      toast.success(t('subscribe.cancelled'));
      await Promise.all([refresh(), load()]);
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  const status = subscriptionStatus(user);
  const source = sub?.source || '';
  const storeLink = STORE_LINKS[source];
  const canCancelHere = Boolean(sub?.can_cancel_here);
  const isCancelled = sub?.status === 'cancelled';

  return (
    <>
      <header className="page-head">
        <h1>{t('subscription.title')}</h1>
      </header>

      {confirming && (
        <section className="card confirming-card">
          <span className="spinner small" aria-hidden="true" />
          <div>
            <strong>{t('subscribe.checkingPayment')}</strong>
            <p className="muted">{t('subscribe.checkingBody')}</p>
          </div>
        </section>
      )}

      <section className={`card status-card ${status.state}`}>
        <span className="status-ic" aria-hidden="true"><Icons.Crown size={22} /></span>
        <div>
          <strong>{t(`subscription.${status.state}`)}</strong>
          {status.state === 'active' && (
            <p className="muted">
              {isCancelled
                ? t('subscribe.endsOn', { date: formatDate(status.expiresAt) })
                : t('subscription.expiresOn', { date: formatDate(status.expiresAt) })}
            </p>
          )}
          {status.state === 'expired' && (
            <p className="muted">{t('subscription.expiredOn', { date: formatDate(status.expiresAt) })}</p>
          )}
          {status.state === 'none' && <p className="muted">{t('subscribe.noneBody')}</p>}
        </div>
      </section>

      {/* Managing an existing subscription — routed by where it was bought. */}
      {status.state === 'active' && (
        <section className="card">
          <h2>{t('subscription.manageTitle')}</h2>

          {canCancelHere && !isCancelled && (
            <>
              <p className="muted">{t('subscribe.manageWebBody')}</p>
              <button type="button" className="btn danger" onClick={cancel} disabled={busy}>
                {t('subscribe.cancelCta')}
              </button>
            </>
          )}

          {isCancelled && (
            <p className="muted">{t('subscribe.alreadyCancelled', { date: formatDate(sub?.current_period_end) })}</p>
          )}

          {!canCancelHere && !isCancelled && (
            <>
              <p className="muted">
                {storeLink ? t(`subscribe.manageStore_${source}`) : t('subscription.manageBody')}
              </p>
              <div className="row gap">
                {storeLink ? (
                  <a className="btn" href={storeLink} target="_blank" rel="noreferrer">
                    {t(`subscription.${source === 'apple' ? 'appStore' : 'playStore'}`)}
                  </a>
                ) : (
                  <>
                    <a className="btn" href={STORE_LINKS.apple} target="_blank" rel="noreferrer">
                      {t('subscription.appStore')}
                    </a>
                    <a className="btn" href={STORE_LINKS.google} target="_blank" rel="noreferrer">
                      {t('subscription.playStore')}
                    </a>
                  </>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* Buying. Hidden while a non-cancelled subscription is live so we don't
          invite someone to pay twice for the same thing. */}
      {(status.state !== 'active' || isCancelled) && (
        <section className="card">
          <h2>{t('subscribe.title')}</h2>
          {loading ? (
            <p className="muted">{t('common.loading')}</p>
          ) : plans.length === 0 ? (
            <p className="muted">{t('subscribe.noPlans')}</p>
          ) : (
            <>
              <ul className="plan-grid">
                {plans.map((p) => (
                  <PlanCard
                    key={p.slug}
                    plan={p}
                    lang={lang}
                    busy={busy}
                    currentPlan={sub?.plan}
                    onChoose={choose}
                  />
                ))}
              </ul>
              {/* Required disclosure: the card is stored and renews
                  automatically. There is no opt-out checkbox by design, which
                  is exactly why the terms have to be stated plainly. */}
              <p className="renew-note">{t('subscribe.autoRenewNotice')}</p>
            </>
          )}
        </section>
      )}
    </>
  );
}
